import React, { memo, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Card, CardActionArea, Chip, Typography } from '@mui/material';
import { ConfirmIcon, MinusCircleIcon, SuccessIcon } from '@sharedCR/coreDesignSystem/icons';
import { emailNotificationsNotificationsApi } from 'app/services/api';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { getDefaultErrorMessage } from 'utils/axios';
import { downloadOrShareBlob } from 'utils/nativeFile';

import { openDialogAction, showErrorMessageAction } from 'store/global/actions';
import { getDebouncedSearchValue } from 'store/search/selectors';

import { EmailStatus } from '../../../Activities.constants';
import HighlightText from '../../../HighlightText';

import EmailStatusPopup from './EmailStatusPopup';

const defaultArray = [];

const EmailFooter = ({
  applicationId,
  attachmentNames = defaultArray,
  emailId,
  events = defaultArray,
}) => {
  const dispatch = useDispatch();

  const highlightText = useSelector(getDebouncedSearchValue);

  const countEventsByType = useMemo(() => _.countBy(events, 'type'), [events]);

  const isIncomingEmail = countEventsByType[EmailStatus.RECEIVED] === 1;

  const onDownloadAttachment = useCallback(
    e => {
      const { attachmentName, decodedAttachmentName } = e.currentTarget.dataset;

      emailNotificationsNotificationsApi
        .getAttachment({
          emailId,
          attachmentName,
        })
        .then(({ data }) => {
          const blob = new Blob([data]);

          downloadOrShareBlob(blob, decodedAttachmentName);
        })
        .catch(err => {
          dispatch(showErrorMessageAction({ message: getDefaultErrorMessage(err), err }));
        });
    },
    [dispatch, emailId],
  );

  const onStatusClick = useCallback(() => {
    dispatch(
      openDialogAction({
        header: 'Email status',
        body: dialogProps => (
          <EmailStatusPopup
            {...dialogProps}
            applicationId={applicationId}
            emailId={emailId}
            events={events}
          />
        ),
      }),
    );
  }, [dispatch, applicationId, emailId, events]);

  const renderStatus = useCallback((title, IconComponent, iconClassName) => {
    return (
      <div className="flex items-center rounded-12 mx-5">
        <IconComponent className={iconClassName} />
        <Typography className="ml-2" color="textSecondary" variant="medium14">
          {title}
        </Typography>
      </div>
    );
  }, []);

  const renderStatusBlock = () => {
    const openedCount = countEventsByType[EmailStatus.OPENED] || 0;

    if (isIncomingEmail) {
      return (
        <CardActionArea className="flex py-4" onClick={onStatusClick}>
          {renderStatus('Received', ConfirmIcon, 'text-green')}
        </CardActionArea>
      );
    }

    if (!openedCount) {
      return <div className="py-4">{renderStatus('Sent', ConfirmIcon, 'text-green')}</div>;
    }

    const clickedCount = countEventsByType[EmailStatus.CLICKED] || 0;

    return (
      <CardActionArea className="flex py-4" onClick={onStatusClick}>
        {renderStatus(
          `Open: ${openedCount}`,
          SuccessIcon,
          openedCount ? 'text-green' : 'text-grey',
        )}
        {renderStatus(
          `Click: ${clickedCount}`,
          MinusCircleIcon,
          clickedCount ? 'text-green' : 'text-grey',
        )}
      </CardActionArea>
    );
  };

  return (
    <div className="flex justify-between">
      <div>
        {attachmentNames?.map(attachment => {
          const decodedName = decodeURIComponent(attachment);

          return (
            <Chip
              className="mb-12 mr-12"
              data-attachment-name={attachment}
              data-decoded-attachment-name={decodedName}
              key={decodedName}
              label={<HighlightText highlight={highlightText} text={decodedName} />}
              size="small"
              onClick={onDownloadAttachment}
            />
          );
        })}
      </div>

      <Card className="self-end flex-none rounded-12 shadow-none -mr-20">
        {renderStatusBlock()}
      </Card>
    </div>
  );
};

EmailFooter.propTypes = {
  applicationId: PropTypes.string.isRequired,
  emailId: PropTypes.string.isRequired,
  attachmentNames: PropTypes.arrayOf(PropTypes.string),
  events: PropTypes.arrayOf(PropTypes.object),
};

export default memo(EmailFooter);
